import {
  EnumTagSemanticoDTOElementoRicerca,
  EnumTagSemanticoDTOTipo,
  TagSemanticoDTO,
} from '@framework/api-client';
import { cloneDeep } from 'lodash';
import {
  ISemanticFieldSearch,
  SemanticFieldSearchOperator,
} from './ISemanticFieldSearch';
import {
  searchableFieldFromName,
  searchableFieldToName,
} from './mapSemanticTagsToSearchableFields';

function getSemanticTagFilterOperator(
  semanticTag: TagSemanticoDTO,
  values: Record<string, any>,
): ISemanticFieldSearch[] {
  if (!semanticTag.nome) return [];

  if (
    semanticTag.elementoRicerca ===
    EnumTagSemanticoDTOElementoRicerca.RICERCA_CON_MENU_TENDINA
  ) {
    const value = values[semanticTag.nome];
    if (value == null) return [];
    return [
      {
        chiave: semanticTag.id,
        operatore: SemanticFieldSearchOperator.EQUALS,
        valore: value,
      },
    ];
  }

  if (
    semanticTag.elementoRicerca ===
    EnumTagSemanticoDTOElementoRicerca.RICERCA_LIBERA
  ) {
    const value = values[semanticTag.nome];
    if (value == null) return [];
    return [
      {
        chiave: semanticTag.id,
        operatore:
          semanticTag.tipo === EnumTagSemanticoDTOTipo.TEXT
            ? SemanticFieldSearchOperator.CONTAINS
            : SemanticFieldSearchOperator.EQUALS,
        valore: value,
      },
    ];
  }

  if (
    semanticTag.elementoRicerca ===
    EnumTagSemanticoDTOElementoRicerca.RICERCA_PER_INTERVALLO
  ) {
    const fromName = searchableFieldFromName(semanticTag.nome);
    const toName = searchableFieldToName(semanticTag.nome);
    const result = [];

    if (values[fromName] != null) {
      result.push({
        chiave: semanticTag.id,
        operatore: SemanticFieldSearchOperator.GREATER_OR_EQUAL,
        valore: values[fromName],
      });
    }
    if (values[toName] != null) {
      result.push({
        chiave: semanticTag.id,
        operatore: SemanticFieldSearchOperator.LESSER_OR_EQUAL,
        valore: values[toName],
      });
    }

    return result;
  }

  return [];
}

/**
 * Funzione di format che trasforma l'oggetot con i filtri di ricerca nella query da inviare al server
 * { [semanticTag]: ..., [semanticTag]: ..., <others>} -> { <others>, campiSemantici: [{chiave: 'semanticTag', operatore: <...>, valore: ...}]}
 */
export function formatSemanticTagSearchable<
  T extends { campiSemantici?: string },
>(
  values: Record<string, any>,
  semanticTags: TagSemanticoDTO[] | null | undefined,
): T {
  // Recupero la lista dei tag semantici esistenti (i campi di tipo intervallo hanno 2 chiavi separate)
  const semanticTagKeys =
    semanticTags
      ?.map(tag => {
        if (
          tag.elementoRicerca ===
          EnumTagSemanticoDTOElementoRicerca.RICERCA_PER_INTERVALLO
        ) {
          return [
            searchableFieldFromName(tag.nome),
            searchableFieldToName(tag.nome),
          ];
        } else {
          return [tag.nome];
        }
      })
      .flatMap(t => t)
      .filter(t => Boolean(t)) ?? ([] as string[]);

  // Clono i valori per poterli modificare
  const valuesCopy = cloneDeep(values);
  const staticValues: Record<string, any> = {};

  // rimuovo i campi _non semantici_ (statici) così da avere solo i campi semantici
  for (const key in values) {
    if (!semanticTagKeys.includes(key)) {
      staticValues[key] = values[key];
      delete valuesCopy[key];
    }
  }

  // Creo la lista dei campi semantici
  const semanticTagQueryList =
    semanticTags
      ?.map(tag => {
        return getSemanticTagFilterOperator(tag, values).flatMap(f => f);
      })
      .flatMap(f => f) ?? [];

  const result = {
    ...staticValues,
    // NB: anche se indicato come un array di oggetti, l'endpoint specifica nella descrizione
    // che è richiesto come stringa in formato JSON.
    campiSemantici: JSON.stringify(semanticTagQueryList),
  };

  return result as T;
}
